import { RefreshCcw, X } from "lucide-react";
import React from "react";
import { useImmer } from "use-immer";

import {
  IgAnalyticsTooltip,
  IgChip,
  IgIconButton,
} from "@intelligems/ig-theme";
import { ExperienceFe } from "@intelligems/ig-types";
import { getFirstStartedAtTs } from "@intelligems/ig-utils";
import { Box, Grid } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import {
  DashboardPageName,
  DashboardRenderFlags,
} from "@src/api/types/dashboards";
import { AnalyticsFilterDrawerWrapper } from "@src/features/analytics/analyticsFilters/AnalyticsFilterDrawerWrapper";
import { SitewideAnalyticsFilterDrawerWrapper } from "@src/features/analytics/analyticsFilters/SitewideAnalyticsFilterDrawerWrapper";
import { AnalyticsDateRange } from "@src/features/analytics/analyticsHeader/AnalyticsDateRange";
import { AnalyticsDateRangeModal } from "@src/features/analytics/analyticsHeader/AnalyticsDateRangeModal";
import { AnalyticsSubpageSelect } from "@src/features/analytics/analyticsHeader/AnalyticsSubpageSelect";
import { FiltersButton } from "@src/features/analytics/analyticsHeader/FiltersButton";
import { DataExportDialog } from "@src/features/analytics/baseDashboard/DataExportDialog";
import { useGetAnalyticsCountries } from "@src/features/analytics/hooks/use-get-analytics-countries";
import { SitewideDateRangePicker } from "@src/features/analytics/sitewideAnalytics/SitewideDateRangePicker";
import { useDispatch, useSelector } from "@src/store";
import {
  refreshData,
  selectedPageName,
  updateDashboardFilter,
} from "@src/store/slices/dashboardFilters";
import { isDrawerOpen } from "@src/store/slices/drawers";
import { getCustomMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { updateSitewideDashboardFilter } from "@src/store/slices/sitewideDashboardFilters";
import {
  AnalyticsActiveFilterType,
  IExperienceDashboardFilters,
  initialDashboardFiltersState,
} from "@src/store/types/dashboardFilters";
import {
  initialSitewideDashboardFiltersState,
  ISitewideDashboardFilters,
} from "@src/store/types/sitewideDashboardFilters";
import { useQueryClient } from "@tanstack/react-query";

import { getFilterLabel } from "./utils";

interface AnalyticsStickyHeaderBaseProps {
  renderLocation: "sitewide" | "experience";
  experience?: ExperienceFe;
  activeFilterArray: AnalyticsActiveFilterType[];
  shouldNotRenderGraphs?: DashboardRenderFlags;
  isDataLoading: number;
  pageName: DashboardPageName;
  filters: IExperienceDashboardFilters | ISitewideDashboardFilters;
  countryCodesData: string[];
}

export const AnalyticsStickyHeaderBase = ({
  renderLocation,
  experience,
  activeFilterArray,
  shouldNotRenderGraphs,
  isDataLoading,
  pageName,
  filters,
  countryCodesData,
}: AnalyticsStickyHeaderBaseProps) => {
  const dispatch = useDispatch();
  const initialFiltersState =
    renderLocation === "sitewide"
      ? initialSitewideDashboardFiltersState
      : initialDashboardFiltersState;
  const filteredFilters = activeFilterArray.filter(
    (filter) => filter.key !== "isZScoreEnabled"
  );

  const analyticsFilterDrawerOpen = useSelector(
    isDrawerOpen("analyticsFilters")
  );
  const queryClient = useQueryClient();
  const customMetrics = useSelector(getCustomMetrics);
  const hasDataReconciliation = useConfiguration(
    "view_data_reconciliation_analytics"
  );
  const [anchorEl, setAnchorEl] = useImmer<HTMLDivElement | null>(null);
  const handlePopoverClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onFilterChipDelete = (key: string) => {
    if (key === "testOrdersOnly") {
      dispatch(
        updateDashboardFilter({
          key,
          value:
            (initialFiltersState[key] === undefined ||
              initialFiltersState[key] === null) &&
            "testOrdersOnly" in filters
              ? !filters.testOrdersOnly
              : !initialFiltersState[key],
        })
      );
    } else if (key === "startTime") {
      if (renderLocation === "sitewide") {
        dispatch(
          updateSitewideDashboardFilter({
            key,
            value: null,
          })
        );
      } else {
        dispatch(
          updateDashboardFilter({
            key,
            value:
              experience?.category === "personalization"
                ? getFirstStartedAtTs(experience.experienceActions)
                : null,
          })
        );
      }
    } else {
      if (renderLocation === "sitewide") {
        dispatch(
          updateSitewideDashboardFilter({
            key: key as keyof ISitewideDashboardFilters,
            value:
              key === "isZScoreEnabled"
                ? !initialFiltersState[key]
                : initialFiltersState[key],
          })
        );
      } else {
        dispatch(
          updateDashboardFilter({
            key: key as keyof IExperienceDashboardFilters,
            value:
              key === "isZScoreEnabled"
                ? !initialFiltersState[key]
                : initialFiltersState[key],
          })
        );
      }
    }
  };

  return (
    <Grid
      container
      className={"analytics-sticky-header"}
      id={"analytics-header"}
      direction={"row"}
      alignItems="center"
      justifyContent="space-between"
      sx={{
        paddingBottom: "10px",
        paddingTop: "10px",
        width: "100%",
        height: "72px",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
      }}
    >
      <Grid
        item
        xs={3}
        sx={{ marginLeft: renderLocation === "sitewide" ? 0 : "35px" }}
      >
        {renderLocation === "sitewide" ||
        (experience && experience.category === "experiment") ? (
          <AnalyticsSubpageSelect
            renderLocation={renderLocation}
            pageName={pageName}
          />
        ) : null}
      </Grid>

      <Grid
        item
        container
        flex={1}
        mr={6.5}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        columnSpacing={2}
      >
        <Grid item mr={1.5}>
          <Grid item container direction="row" wrap="nowrap" spacing={1}>
            {filteredFilters.slice(0, 3).map((filter) => (
              <Grid item key={filter.key || "key"}>
                <IgChip
                  onDelete={() => {
                    onFilterChipDelete(
                      filter.key as keyof (
                        | IExperienceDashboardFilters
                        | ISitewideDashboardFilters
                      )
                    );
                  }}
                  variant={"outlined"}
                  size={"small"}
                  deleteIcon={<X size={12} />}
                  label={getFilterLabel(filter, customMetrics)}
                />
              </Grid>
            ))}
            {filteredFilters.length > 3 && (
              <Grid item>
                <IgChip
                  size={"small"}
                  variant={"outlined"}
                  deleteIcon={<X size={12} />}
                  label={`+${filteredFilters.length - 3}`}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Box
          data-testid="analytics-controls"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            columnGap: 2,
          }}
        >
          {!shouldNotRenderGraphs?.[pageName] && (
            <Grid item>
              <FiltersButton activeFilterArray={filteredFilters} />
            </Grid>
          )}
          {!shouldNotRenderGraphs?.[pageName] && (
            <Grid
              data-testid="analytics-date-range"
              item
              onClick={(event) => {
                handlePopoverClick(event);
              }}
              sx={{ "&:hover": { cursor: "pointer" } }}
            >
              {experience ? (
                <AnalyticsDateRange experience={experience} />
              ) : (
                <SitewideDateRangePicker />
              )}
            </Grid>
          )}
        </Box>

        {anchorEl && experience ? (
          <AnalyticsDateRangeModal
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            experience={experience}
          />
        ) : null}
        <Grid item>
          <IgAnalyticsTooltip
            title={
              isDataLoading > 0
                ? "Please wait for the data to return before refreshing"
                : "Refresh Data"
            }
          >
            <IgIconButton
              disabled={isDataLoading > 0}
              onClick={() => {
                dispatch(refreshData());
                void queryClient.invalidateQueries({
                  predicate: (query) =>
                    query.queryKey.some(
                      (key) =>
                        typeof key === "object" &&
                        JSON.stringify(key).includes("dashboards")
                    ),
                });
              }}
            >
              <RefreshCcw size={20} />
            </IgIconButton>
          </IgAnalyticsTooltip>
        </Grid>
      </Grid>
      {experience && (
        <DataExportDialog
          experience={experience}
          showDataReconciliationExport={hasDataReconciliation?.value ?? false}
        />
      )}
      {renderLocation === "experience" && analyticsFilterDrawerOpen ? (
        <AnalyticsFilterDrawerWrapper
          renderLocation={renderLocation}
          openFilters={analyticsFilterDrawerOpen}
          pageName={pageName}
          currentExperience={experience}
          allCountryCodesData={countryCodesData}
        />
      ) : renderLocation === "sitewide" && analyticsFilterDrawerOpen ? (
        <SitewideAnalyticsFilterDrawerWrapper
          openFilters={analyticsFilterDrawerOpen}
          pageName={pageName}
          allCountryCodesData={countryCodesData}
        />
      ) : null}
    </Grid>
  );
};
